import { portalPostMessage } from "../../../api/clientPortal";
import { portalUploadOne, inferPortalFileMessageType } from "../../../api/portalUpload";
import { getClientPortalErrorMessage } from "../clientPortalErrorMessage";
import { mergePortalMessages } from "./portalMessagesState";
import {
  buildOptimisticPortalMessage,
  createOptimisticPortalTempId,
  revokeOptimisticPortalBlobUrl,
} from "./portalOptimisticMessage";

async function blobFromObjectUrl(objectUrl) {
  const res = await fetch(objectUrl);
  return res.blob();
}

async function preparePortalApiPayload(payload = {}) {
  const { _file, _audioURL, message } = payload;
  const caption = String(message || "").trim();

  if (_audioURL) {
    const blob = await blobFromObjectUrl(_audioURL);
    const audioFile = new File([blob], `audio-${Date.now()}.mp3`, {
      type: blob.type && blob.type !== "audio/mp3" ? blob.type : "audio/mpeg",
    });
    const uploaded = await portalUploadOne(audioFile);
    return { type: "audio", audio: uploaded.url, message: caption };
  }

  if (_file) {
    const uploaded = await portalUploadOne(_file);
    return {
      type: inferPortalFileMessageType(_file),
      message: caption || _file.name || "",
      file: {
        url: uploaded.url,
        name: _file.name,
        type: _file.type,
        size: _file.size,
      },
    };
  }

  if (caption) {
    return { message: caption };
  }

  throw Object.assign(new Error("Nada para enviar."), { isPortalClientError: true });
}

export async function executeOptimisticPortalSend({
  ticketId,
  payload,
  setMessages,
  syncMessages,
}) {
  const tempId = createOptimisticPortalTempId();
  const optimistic = buildOptimisticPortalMessage(payload, { tempId });

  setMessages((prev) => mergePortalMessages(prev, [optimistic], "merge"));

  try {
    const apiPayload = await preparePortalApiPayload(payload);
    const result = await portalPostMessage(ticketId, apiPayload);
    const savedId = result?.id ?? result?.message?.id;

    revokeOptimisticPortalBlobUrl(optimistic);

    const confirmed = {
      ...optimistic,
      id: savedId ?? optimistic.id,
      sendStatus: undefined,
      _optimistic: undefined,
      _localBlobUrl: undefined,
      errorMessage: undefined,
    };

    setMessages((prev) => {
      const withoutTemp = prev.filter((m) => m.id !== tempId);
      return mergePortalMessages(withoutTemp, [confirmed], "merge");
    });

    syncMessages?.().catch(() => {});

    return result;
  } catch (err) {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === tempId
          ? {
              ...m,
              sendStatus: "error",
              errorMessage: getClientPortalErrorMessage(err, { context: "sendMessage" }),
            }
          : m
      )
    );
    throw err;
  }
}
